import type { MessageKey, Params } from '@lensmira/i18n'

import type { QueueSnapshot } from './types.js'

/**
 * What the shop is told about the write queue. `OFF-005`, `LOC-001`.
 *
 * `OFF-005` `MUST`: "Visible connection state and pending-sync count. Staff must never be uncertain
 * whether a sale is safe." Five screens render that sentence and **all five held their own copy of
 * this logic**, each with its own comment explaining the same two rules. `JobDetail`'s copy said so
 * in as many words: "kept local rather than shared: there is nowhere in this codebase yet that both
 * screens import from, and duplicating ten lines is cheaper than inventing that seam for one
 * caller." That was true of the second copy and stopped being true somewhere around the fourth.
 *
 * Localisation is what forced the issue: five copies of a concatenated sentence is five places to
 * translate and five chances for one of them to keep an English fragment. One of them had a real
 * divergence too, which nobody had noticed: `RxCapture` and `TakePayment` said "This prescription
 * was NOT saved" where the other three said "The order was NOT saved", so the same event read two
 * ways depending on which screen you were on.
 *
 * **The subject is passed in, already translated.** A sentence is never built from two `t()` calls
 * (Appendix S), and this does not: the subject is a noun phrase the screen names, and the message
 * is a whole sentence with a `{subject}` hole. In a right-to-left locale `translate` wraps it in a
 * first-strong isolate, so an English product name inside an Urdu sentence does not drag the
 * punctuation around it (`RTL-03`).
 */

export type Translate = (key: MessageKey, params?: Params) => string

/**
 * The connection and the pending count, as one sentence.
 *
 * The blocked count is in here and not only in the per-write clause, because a write blocked in an
 * earlier session is still on this device and still nobody's. Reloading the app and reading
 * "everything synced" over a refused prescription is the lie `OFF-005` exists to prevent.
 */
export function connectionLine(t: Translate, snapshot: QueueSnapshot): string {
  const connection = t(snapshot.online ? 'queue.online' : 'queue.offline')
  const waiting = snapshot.pending + snapshot.sending
  const parts: string[] = []
  if (waiting > 0) parts.push(t('queue.waitingToSync', { count: waiting }))
  if (snapshot.blocked > 0) parts.push(t('queue.needAttention', { count: snapshot.blocked }))
  return parts.length > 0
    ? t('queue.busy', { connection, detail: parts.join(', ') })
    : t('queue.synced', { connection })
}

/**
 * One write's fate, as one sentence, or `null` when this screen has not queued anything.
 *
 * Never the word "saved" before the server has said so. A write that has aged out of the retention
 * window reads as saved, because the window is long after acceptance and a write still in flight
 * is still in the snapshot.
 */
/**
 * Why a write was refused, in one clause with no trailing stop.
 *
 * **Blank-checked, not null-checked.** The five copies this replaced read
 * `error?.message ?? error?.code ?? 'refused by the server'`, and the middle term was unreachable:
 * `QueuedWrite['error']` makes `code` and `message` both required when `error` is present, so
 * `message` is undefined only when `code` is too. An **empty** message is what the chain was
 * actually for, and `??` does not catch one. A refusal rendered as "The order was NOT saved: . It
 * needs attention." is a sentence with a hole in it, and a shopkeeper cannot tell that from a bug.
 */
function reasonOf(
  write: { readonly error?: { readonly code: string; readonly message: string } },
  t: Translate,
): string {
  const message = write.error?.message?.trim() ?? ''
  const code = write.error?.code?.trim() ?? ''
  const text = message !== '' ? message : code !== '' ? code : t('queue.write.refusedNoReason')
  // U+06D4 ARABIC FULL STOP as well as the ASCII one: an Urdu message from the server ends in it.
  return text.replace(/[.\u06d4]+$/, '')
}

export function writeLine(
  t: Translate,
  snapshot: QueueSnapshot,
  writeId: string | null,
  subject: string,
): string | null {
  if (!writeId) return null
  const write = snapshot.writes.find((w) => w.id === writeId)
  if (!write) return t('queue.write.saved', { subject })
  if (write.status === 'blocked') {
    return t('queue.write.blocked', {
      subject,
      // The trailing stop is stripped because the message supplies its own. Appendix F's customer
      // copy is written as sentences, so every refusal with a real message rendered a double full
      // stop on the till: "A sphere is required for either eye.. It needs attention." All five
      // copies of this line did it and no test noticed, because they all matched on a prefix.
      reason: reasonOf(write, t),
    })
  }
  if (write.status === 'sent') return t('queue.write.saved', { subject })
  return t('queue.write.pending', { subject })
}

/**
 * Both, joined, which is what four of the five screens render in one `role="status"` paragraph.
 *
 * The join is punctuation between two finished sentences rather than sentence building: a locale
 * that separates them differently changes `queue.statusLine` and nothing else.
 */
export function statusLine(
  t: Translate,
  snapshot: QueueSnapshot,
  writeId: string | null,
  subject: string,
): string {
  const queueState = connectionLine(t, snapshot)
  const write = writeLine(t, snapshot, writeId, subject)
  return write === null ? queueState : t('queue.statusLine', { queueState, writeState: write })
}
